import {
  Alert,
  Box,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import {useCallback, useEffect, useRef, useState} from 'react';
import {
  IParameterDetailsProp,
  IPartnerProps,
  IKeyPartnersProps,
} from '../TnnCampaignCreationStepper';
import {SponsorFormState} from '../task/cmpt/AddSponsorStep';
import useDebounce from '../../../../components/utlis/useDebounce';
import {CustomTextarea} from '../../../../components/styles/CustomTextarea';
import {apiPut} from '../../../../axiosConfig';
import {ICompany} from '../../../../dto/Company.dto';
import {TnnReviewSummary} from './TnnReviewSummary';

export const ReviewCmpt = ({
  campaignName,
  adSource,
  campaignType,
  commercialLength,
  type,
  parameter,
  partners,
  setPartners,
  sponsorForm,
  isReview = true,
  notes,
  sponsorRequestId,
  scheduleId,
  campaignReviewLayout = false,
  hubspotLayout = false,
}: {
  campaignName: string;
  adSource: string;
  type: string;
  commercialLength: string;
  campaignType: string[];
  parameter: IParameterDetailsProp;
  partners: IPartnerProps;
  setPartners?: React.Dispatch<React.SetStateAction<IPartnerProps>>;
  sponsorForm?: SponsorFormState;
  isReview?: boolean;
  notes?: string;
  sponsorRequestId?: number;
  scheduleId?: number;
  campaignReviewLayout?: boolean;
  hubspotLayout?: boolean;
}) => {
  const notesField =
    notes !== undefined && sponsorRequestId && scheduleId ? (
      <DebouncedTextField
        notes={notes}
        url={`v1/tnn/sponsor/${sponsorRequestId}/campaign/${scheduleId}/notes`}
      />
    ) : null;

  return (
    <Box sx={{width: '100%', minWidth: 0}}>
      <TnnReviewSummary
        sponsorForm={sponsorForm}
        campaignName={campaignName}
        adSource={adSource}
        commercialLength={commercialLength}
        type={type}
        campaignType={campaignType}
        parameter={parameter}
        partners={partners}
        setPartners={setPartners}
        isReview={isReview}
        campaignReviewLayout={campaignReviewLayout}
        firstColumnContent={campaignReviewLayout ? notesField : undefined}
        hubspotLayout={hubspotLayout}
      />
      {!campaignReviewLayout && notesField ? (
        <Box sx={{mt: 2}}>{notesField}</Box>
      ) : null}
    </Box>
  );
};

export const CompanyListCmpt = ({
  title,
  companies,
}: {
  title: string;
  companies: ICompany[];
}) => (
  <Box sx={{width: '100%', maxWidth: 360}}>
    <Typography sx={{fontSize: 16, fontWeight: 600, mb: 1}}>
      {`${title} (${companies.length})`}
    </Typography>
    <List
      className='scrollbar'
      sx={{
        maxHeight: 275,
        overflow: 'auto',
        backgroundColor: 'background.default',
        borderRadius: 1,
        p: 1,
      }}>
      {companies.map(company => (
        <ListItemButton key={company.id} dense divider>
          <ListItemText primary={company.name} />
        </ListItemButton>
      ))}
    </List>
  </Box>
);

export const DebouncedTextField = ({
  notes,
  url,
}: {
  notes: string;
  url: string;
}) => {
  const [newNotes, setNewNotes] = useState(notes);
  const [isLoading, setIsLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const saveQueueRef = useRef(Promise.resolve());
  const saveRequestIdRef = useRef(0);

  const debouncedText = useDebounce(newNotes, 1000);

  const handleSubmit = useCallback(() => {
    const requestId = ++saveRequestIdRef.current;
    const notesToSave = debouncedText;
    setIsLoading(true);
    setSaveError(null);

    saveQueueRef.current = saveQueueRef.current
      .catch(() => undefined)
      .then(async () => {
        try {
          await apiPut(url, {
            notes: notesToSave,
          });
        } catch {
          if (requestId === saveRequestIdRef.current) {
            setSaveError('Unable to save note. Please try again.');
          }
        } finally {
          if (requestId === saveRequestIdRef.current) {
            setIsLoading(false);
          }
        }
      });

    return saveQueueRef.current;
  }, [url, debouncedText]);

  const handleRetry = useCallback(() => {
    void handleSubmit();
  }, [handleSubmit]);

  useEffect(() => {
    if (debouncedText === notes) return;
    void handleSubmit();
  }, [debouncedText, handleSubmit, notes]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setNewNotes(e.target.value);
      setSaveError(null);
    },
    [],
  );

  return (
    <Box position='relative' width='100%' height='375px'>
      <CustomTextarea
        id='message-description'
        placeholder='Enter Note here'
        onChange={handleChange}
        value={newNotes}
        sx={{
          height: '100%',
          m: 0,
          p: 0,
          '& .MuiInputBase-root': {
            height: '100%',
            alignItems: 'stretch',
            backgroundColor: 'transparent',
          },
        }}
      />
      {isLoading ? (
        <Box
          position='absolute'
          bottom={8}
          right={8}
          display='flex'
          alignItems='center'
          justifyContent='center'>
          <span className='text-white-50'>Saving…</span>
        </Box>
      ) : null}
      {saveError ? (
        <Alert
          severity='error'
          action={
            <Button color='inherit' size='small' onClick={handleRetry}>
              Retry
            </Button>
          }
          sx={{mt: 1}}>
          {saveError}
        </Alert>
      ) : null}
    </Box>
  );
};

export type {IKeyPartnersProps};
